import { limiter } from '../../lib/limit';
import { preparePhoto, prepareThumb } from '../../lib/image';
import { getBlob, putBlob } from './blobStore';

/**
 * The urls handed out for pictures, remembered — but not for ever.
 *
 * An object url keeps the bytes behind it alive until it is revoked, and these
 * used to be kept in a plain Map for the whole session. That was invisible with
 * a demo family's forty photographs. With a real family's it is a leak with no
 * ceiling: every picture anybody opens stays in the phone's memory until the tab
 * is closed, and an evening spent going through a wedding is hundreds of
 * full-size photographs.
 *
 * So this remembers the most recently *used* ones and lets the oldest go,
 * revoking as it does. Letting go is safe: an `<img>` that has already loaded
 * keeps its pixels, and one that is pointed at a revoked url later simply fails,
 * says so, and is given a fresh one (`forget` — the same self-healing every
 * picture in the app already has for a url that went stale by itself).
 */
export class UrlCache {
  private urls = new Map<string, string>();
  /** One reading of a picture at a time, however many circles ask for it in the same moment. */
  private asking = new Map<string, Promise<string | undefined>>();

  constructor(private max: number) {}

  /**
   * The url for this picture: the remembered one, or the one already on its
   * way, or a new one from `make`. Two callers must never each be given a url
   * of their own — the second would replace (and so revoke) the first while it
   * is still being drawn.
   */
  resolve(key: string, make: () => Promise<string | undefined>): Promise<string | undefined> {
    const cached = this.get(key);
    if (cached) return Promise.resolve(cached);
    const already = this.asking.get(key);
    if (already) return already;
    const work = make()
      .then((url) => {
        if (url) this.set(key, url);
        return url;
      })
      .finally(() => this.asking.delete(key));
    this.asking.set(key, work);
    return work;
  }

  /** Reading one marks it as just used (a Map keeps insertion order, so re-inserting moves it to the young end). */
  get(key: string): string | undefined {
    const url = this.urls.get(key);
    if (url === undefined) return undefined;
    this.urls.delete(key);
    this.urls.set(key, url);
    return url;
  }

  set(key: string, url: string): void {
    const was = this.urls.get(key);
    if (was && was !== url) revoke(was);
    this.urls.delete(key);
    this.urls.set(key, url);
    while (this.urls.size > this.max) {
      const oldest = this.urls.keys().next().value as string;
      revoke(this.urls.get(oldest)!);
      this.urls.delete(oldest);
    }
  }

  /** "That url does not work": drop it and let the browser release what is behind it. */
  forget(key: string): void {
    const url = this.urls.get(key);
    if (url === undefined) return;
    this.urls.delete(key);
    revoke(url);
  }
}

function revoke(url: string): void {
  try {
    URL.revokeObjectURL(url);
  } catch {
    /* a signed url in production has nothing to revoke */
  }
}

/** Three at a time, the newest batch first — see lib/limit.ts for why both halves of that matter on a phone. */
const shrink = limiter(3);
/**
 * Reading one back is cheap, but a ring that has just opened asks for several
 * hundred in the same instant, and that many storage transactions at once is a
 * stall of its own. A handful at a time keeps them in order too — the middle of
 * the ring first — which is the order anybody is looking in.
 */
const fetchSmall = limiter(8);

/**
 * The small square kept beside a picture, made if it is not there yet.
 *
 * Shared by the family's photographs and the faces on circles, because both are
 * drawn small a great deal more often than they are drawn large: a ring of two
 * hundred photographs, a tree of four hundred faces, a timeline whose every row
 * wears three or four. A face is stored at 640px and a photograph at 1600px —
 * a megabyte and a half, and ten megabytes, of pixels once decoded — so drawing
 * them *as they are* into a 40px circle is what runs a phone out of memory long
 * before it runs out of patience.
 *
 * Returns undefined when there is nothing to shrink or it could not be done;
 * the caller then falls back to the picture itself, which is always better than
 * a blank.
 */
export async function smallCopy(thumbKey: string, fullKey: string, side: number, shape: 'square' | 'whole' = 'square'): Promise<Blob | undefined> {
  // A stored square that came back empty counts as not there (see getBlob): it is made again.
  const kept = await fetchSmall(() => getBlob(thumbKey));
  if (kept) return kept;
  return shrink(async () => {
    // Asked for twice while it waited its turn: the first one through has already made it.
    const made = await getBlob(thumbKey);
    if (made) return made;
    const full = await getBlob(fullKey);
    if (!full) return undefined;
    try {
      // A circle crops to a square anyway, so its copy is one; a card shows the whole picture, so its copy keeps its shape.
      const small = (shape === 'square' ? await prepareThumb(full, side) : await preparePhoto(full, side)).blob;
      // Never keep — and never hand out — an empty square: a url with nothing
      // behind it draws as a broken picture, and a stored one would do it for ever.
      if (small.size === 0) return undefined;
      await putBlob(thumbKey, small);
      return small;
    } catch {
      // A picture the browser cannot decode, or storage that refused the write.
      return undefined;
    }
  });
}
